import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { fireConfetti } from "@/components/Confetti";
import { useT } from "@/lib/i18n";

const SEG_COLORS = ["#7C3AED", "#EC4899", "#F59E0B", "#10B981", "#3B82F6", "#EF4444", "#8B5CFF", "#14B8A6"];

export default function WheelOfFortune({ open, prizes = [], onClose, onResult, subtitle, userId }) {
  const t = useT();
  const tutKey = `slackr_wheel_tut_${userId || "anon"}`;
  // normaliseer naar {label, weight}
  const items = (prizes || []).map((p) => (typeof p === "string" ? { label: p, weight: 1 } : { label: p.label, weight: Math.max(1, p.weight || 0) }))
    .filter((p) => p.label);
  const n = Math.max(1, items.length);
  const totalW = items.reduce((s, p) => s + p.weight, 0) || 1;
  // cumulatieve hoeken (graden) per vakje
  const bounds = [];
  let acc = 0;
  items.forEach((p) => { const start = acc; acc += (p.weight / totalW) * 360; bounds.push([start, acc]); });

  const [rotation, setRotation] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | spinning | stopping | done
  const [result, setResult] = useState(null);
  const rotRef = useRef(0);
  const velRef = useRef(0);
  const rafRef = useRef(null);
  const holdingRef = useRef(false);
  const [showIntro, setShowIntro] = useState(false);
  const [practiceLeft, setPracticeLeft] = useState(0);
  const practiceRef = useRef(0);
  const tutorialRef = useRef(false);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);
  useEffect(() => {
    if (open) {
      setPhase("idle"); setResult(null); setRotation(0); rotRef.current = 0; velRef.current = 0;
      let needTut = false;
      try { needTut = !!onResult && !localStorage.getItem(tutKey); } catch (e) { needTut = !!onResult; }
      tutorialRef.current = needTut;
      practiceRef.current = needTut ? 2 : 0;
      setPracticeLeft(needTut ? 2 : 0);
      setShowIntro(needTut);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const finalize = () => {
    cancelAnimationFrame(rafRef.current);
    // Oefendraai: telt niet mee en heeft geen invloed op de kans
    if (tutorialRef.current && practiceRef.current > 0) {
      practiceRef.current -= 1;
      setPracticeLeft(practiceRef.current);
      setPhase("practice-done");
      fireConfetti();
      return;
    }
    const pointerAngle = (360 - (rotRef.current % 360)) % 360; // vakje onder de pijl (bovenaan)
    let idx = bounds.findIndex(([a, b]) => pointerAngle >= a && pointerAngle < b);
    if (idx < 0) idx = 0;
    const label = items[idx]?.label;
    setResult(label);
    setPhase("done");
    fireConfetti();
    if (tutorialRef.current) { try { localStorage.setItem(tutKey, "1"); } catch (e) { /* noop */ } tutorialRef.current = false; }
    if (onResult) { try { onResult(label); } catch (e) { /* noop */ } }
  };

  const loop = () => {
    if (holdingRef.current) {
      velRef.current = Math.min(velRef.current + 0.9, 26);
    } else {
      velRef.current = velRef.current * 0.985 - 0.06;
      if (velRef.current <= 0.25) { velRef.current = 0; finalize(); return; }
    }
    rotRef.current = (rotRef.current + velRef.current) % 360;
    setRotation(rotRef.current);
    rafRef.current = requestAnimationFrame(loop);
  };

  const startHold = () => {
    if (showIntro) return;
    holdingRef.current = true;
    if (phase === "spinning" || phase === "stopping") return;
    setResult(null);
    setPhase("spinning");
    velRef.current = Math.max(velRef.current, 4);
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
  };
  const releaseHold = () => {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    if (phase === "spinning") setPhase("stopping");
  };

  const gradient = `conic-gradient(${bounds.map(([a, b], i) => `${SEG_COLORS[i % SEG_COLORS.length]} ${a}deg ${b}deg`).join(", ")})`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="wheel-dialog" onClick={onClose}>
      <div className="brutal bg-white rounded-2xl w-full max-w-sm p-5 flex flex-col items-center relative" onClick={(e) => e.stopPropagation()}>
        <div className="w-full flex items-center justify-between mb-2">
          <div>
            <p className="font-head font-black text-lg">🎡 {t("Rad van Fortuin")}</p>
            {subtitle && <p className="text-xs font-bold text-brand-ink/60">{subtitle}</p>}
          </div>
          <button data-testid="wheel-close" onClick={onClose} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><X className="w-4 h-4" strokeWidth={3} /></button>
        </div>

        <div className="relative select-none" style={{ width: 280, height: 300 }}>
          <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ top: 0 }}>
            <div style={{ width: 0, height: 0, borderLeft: "14px solid transparent", borderRight: "14px solid transparent", borderTop: "22px solid #1B1440" }} />
          </div>
          <div
            data-testid="wheel-spinner"
            onPointerDown={startHold}
            onPointerUp={releaseHold}
            onPointerLeave={releaseHold}
            className="absolute cursor-pointer touch-none"
            style={{
              top: 20, left: 0, width: 280, height: 280, borderRadius: "50%",
              background: gradient, transform: `rotate(${rotation}deg)`,
              border: "6px solid #1B1440", boxShadow: "0 6px 0 rgba(27,20,64,.35)",
            }}>
            {items.map((p, i) => {
              const a = (bounds[i][0] + bounds[i][1]) / 2;       // middenhoek vanaf boven (met de klok mee)
              const flip = a > 180;                              // linkerhelft: draai 180° zodat tekst rechtop staat
              const rot = a - 90 + (flip ? 180 : 0);             // radiaal langs de spaak
              const r = 70;                                      // straal tot labelmidden
              const theta = ((a - 90) * Math.PI) / 180;
              const x = 140 + r * Math.cos(theta);
              const y = 140 + r * Math.sin(theta);
              return (
                <span key={i} className="absolute block text-center font-head font-black text-white text-[12px] leading-none"
                  style={{
                    left: x, top: y, width: 120,
                    transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    textShadow: "0 1px 2px rgba(0,0,0,.65)",
                    pointerEvents: "none",
                  }}>{p.label}</span>
              );
            })}
          </div>
          <div className="absolute z-10 left-1/2 rounded-full bg-white brutal-sm"
            style={{ top: 20 + 140 - 22, width: 44, height: 44, transform: "translateX(-50%)" }} />
        </div>

        {phase === "done" && result ? (
          <div className="mt-4 w-full brutal-sm rounded-xl bg-brand-yellow p-3 text-center" data-testid="wheel-result">
            <p className="text-xs font-bold text-brand-ink/60">{t("Gefeliciteerd, je hebt gewonnen")}:</p>
            <p className="font-head font-black text-xl leading-tight mt-0.5">🎉 {result}</p>
            {!onResult && <button data-testid="wheel-again" onClick={startHold} className="mt-2 brutal-sm brutal-press rounded-full px-4 py-1.5 text-xs font-bold bg-white">{t("Nog een keer")}</button>}
          </div>
        ) : phase === "practice-done" ? (
          <div className="mt-4 w-full brutal-sm rounded-xl bg-brand-teal/20 p-3 text-center" data-testid="wheel-practice-done">
            <p className="font-head font-black text-base">{t("Goed geoefend!")} 🎯</p>
            <p className="text-xs font-bold text-brand-ink/60 mt-0.5">
              {practiceLeft > 0
                ? `${t("Nog")} ${practiceLeft} ${t("oefenbeurt — houd het rad opnieuw ingedrukt")}`
                : t("Klaar met oefenen! De volgende draai telt écht. Houd ingedrukt 🎉")}
            </p>
          </div>
        ) : (
          <p className="mt-4 text-sm font-bold text-brand-ink/60 text-center" data-testid="wheel-hint">
            {phase === "spinning"
              ? t("Laat los om te stoppen…")
              : tutorialRef.current
                ? t("Oefenbeurt — houd ingedrukt om te draaien, laat los om te stoppen")
                : t("Houd ingedrukt om te draaien, laat los om te stoppen")}
          </p>
        )}

        {showIntro && (
          <div className="absolute inset-0 z-30 rounded-2xl bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center" data-testid="wheel-intro">
            <p className="text-4xl mb-1">🎡</p>
            <p className="font-head font-black text-xl">{t("Zo werkt het Rad")}</p>
            <p className="text-sm font-bold text-brand-ink/70 mt-2">{t("Houd het rad ingedrukt om te draaien en laat los om te stoppen. Waar de pijl stopt, win je!")}</p>
            <p className="text-xs font-bold text-brand-ink/50 mt-3">{t("Je krijgt eerst 2 oefenbeurten. Die tellen niet mee. Daarna telt het écht.")}</p>
            <button data-testid="wheel-intro-start" onClick={() => setShowIntro(false)} className="mt-4 brutal-sm brutal-press rounded-full px-5 py-2 font-head font-black bg-brand-violet text-white">{t("Start oefenen (2×)")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
